import React from 'react'
import { ErrorState } from './UiStates'

interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Friendly heading shown in the fallback. */
  title?: string
  /** Friendly explanation shown in the fallback. */
  message?: string
  /** Show a retry button that clears the error and re-renders children. */
  onReset?: () => void
  /** Label for the retry button when retry is available. */
  retryLabel?: string
  /**
   * When any value in this array changes, a captured error is cleared so the
   * boundary attempts to render children again. Use it to auto-recover when the
   * surrounding context changes (e.g. switching workspace or route).
   */
  resetKeys?: ReadonlyArray<unknown>
  /** data-testid forwarded to the fallback for tests. */
  testId?: string
}

interface ErrorBoundaryState {
  error: Error | null
  details: string | null
}

const isDev = Boolean(
  typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV,
)

const keysChanged = (a?: ReadonlyArray<unknown>, b?: ReadonlyArray<unknown>) => {
  if (a === b) return false
  if (!a || !b || a.length !== b.length) return true
  for (let i = 0; i < a.length; i += 1) {
    if (!Object.is(a[i], b[i])) return true
  }
  return false
}

/**
 * Catches render/lifecycle errors in its subtree and shows a friendly fallback
 * instead of crashing the whole app. Stack traces are only surfaced in the
 * fallback during development; production fallbacks never expose them.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, details: null }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface the error for diagnostics. Kept lightweight and side-effect-free.
    console.error('ErrorBoundary caught an error', error, info)
    this.setState({
      details: `${error?.stack || error?.message || String(error)}${info?.componentStack || ''}`,
    })
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset()
    }
  }

  reset = () => {
    this.setState({ error: null, details: null })
    this.props.onReset?.()
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorState
          title={this.props.title}
          message={this.props.message}
          retryLabel={this.props.retryLabel}
          onRetry={this.reset}
          details={isDev && this.state.details ? this.state.details : undefined}
          testId={this.props.testId}
        />
      )
    }
    return this.props.children
  }
}
